/*
 *
 * Configurator
 *
 */

import React, { useEffect, useState } from 'react';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectSaga } from 'utils/injectSaga';
import { createStructuredSelector } from 'reselect';
import reducers from './reducers';
import sagas from './sagas';
import Header from 'containers/Header';
import Ribbon from 'containers/Ribbon';
import { useDispatch, useSelector } from 'react-redux';
import { loadFurnitureAction } from 'containers/Configurator/actions/furniture';
import { ApplicationRootState } from 'types';
import { makeSelectIsConfiguratorReady } from 'containers/Configurator/selectors/configurator';
import { withStyles } from '@material-ui/core/styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import {
  makeSelectFurniture,
  makeSelectIsCabinetLoading,
  makeSelectIsCabinetReady,
  makeSelectLoadingFurniture,
  makeSelectSelectedIdCabinet,
} from 'containers/Configurator/selectors/furniture';
import PiecesList from 'containers/PiecesList';
import Viewer from 'containers/Viewer';
import SupportPanel from 'containers/SupportPanel';
import PanesPanel from 'containers/PanesPanel';
import ControlConfigurator from 'containers/ControlConfigurator';
import {
  makeSelectContext,
  makeSelectDisplaySchemaDimension,
  makeSelectIsVisiblePieceList,
  makeSelectIsVisibleViewer,
} from 'containers/Configurator/selectors/general';
import Info from 'containers/Info';
import DrawersCompositionPanel from 'containers/DrawersCompositionPanel';
import IntermediateShelfPanel from 'containers/IntermediateShelfPanel';
import HandlesPanel from 'containers/HandlesPanel';
import PlatePanel from 'containers/PlatePanel';
import DrawersPanel from 'containers/DrawersPanel';
import FurniturePanel from 'containers/FurniturePanel';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';
import useMedia from 'use-media';
import FurnitureInfos from 'containers/FurnitureInfos';
import DimensionInfos from 'containers/DimensionInfos';
import { CircleSpinner } from 'react-spinners-kit';
import IconsCanvas from '../IconsCanvas';
import properties from '../../helpers/config';
import IconBarCanvasMobile from '../IconsBarCanvasMobile';
import TutorialTour from '../TutorialTour';
import CrossPiecePanel from '../CrossPiecePanel';
import AddToCart from '../UserActions/AddToCart';
import PaintPanel from '../PaintPanel';
import Description from '../Description';
import { FurnitureEditorContext } from '../../lib/constraintsChecker/FurnitureEditor';
import SchemaDimensionsContainer from '../SchemaDimensions';
import UpperShelfPanel from '../UpperShelfPanel';
import { Furniture } from '../../lib/modelBuilder/entities/Furniture';
import { Cabinet } from '../../lib/modelBuilder/entities/Cabinet';
import PackagingInfos from '../PackagingInfos';
import TypeCabinetModalSelector from '../TypeCabinetModalSelector';
import { getContext } from '../../helpers/tools';
import { setContextAction } from './actions/general';
import vhCheck from 'vh-check';
import GpuInfo from 'utils/GpuInfo';

interface StateProps {
  isCabinetReady: boolean;
  isConfiguratorReady: boolean;
  isCabinetLoading: boolean;
  isVisiblePieceList: boolean;
  isVisibleViewer: boolean;
  isLoadingViewer: boolean;
  context: FurnitureEditorContext;
  displayDimension: boolean;
  furniture: Furniture | null;
  selectedCabinetId: string | null;
}

// Map RootState to your StateProps
const mapStateToProps = createStructuredSelector<
  ApplicationRootState,
  StateProps
>({
  isConfiguratorReady: makeSelectIsConfiguratorReady(),
  isCabinetLoading: makeSelectIsCabinetLoading(),
  isCabinetReady: makeSelectIsCabinetReady(),
  isVisiblePieceList: makeSelectIsVisiblePieceList(),
  isVisibleViewer: makeSelectIsVisibleViewer(),
  isLoadingViewer: makeSelectLoadingFurniture(),
  context: makeSelectContext(),
  displayDimension: makeSelectDisplaySchemaDimension(),
  furniture: makeSelectFurniture(),
  selectedCabinetId: makeSelectSelectedIdCabinet(),
});

const key = 'configurator';

const ColorLinearProgress = withStyles({
  colorPrimary: {
    backgroundColor: '#aba5a5',
  },
  barColorPrimary: {
    backgroundColor: '#272424',
  },
})(LinearProgress);

function Configurator() {
  useInjectReducer({ key: key, reducer: reducers });
  useInjectSaga({ key: key, saga: sagas });
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isMobile = useMedia({ maxWidth: 1023 });
  const [showTypeSelector, setShowTypeSelector] = useState(false);

  const {
    isConfiguratorReady,
    isCabinetLoading,
    isCabinetReady,
    isVisiblePieceList,
    isVisibleViewer,
    isLoadingViewer,
    context,
    displayDimension,
    furniture,
    selectedCabinetId,
  } = useSelector(mapStateToProps);

  useEffect(() => {
    dispatch(setContextAction(getContext(window.location.href)));

    vhCheck();
  }, []);

  useEffect(() => {
    if (!isConfiguratorReady) {
      if (!isCabinetLoading && !isCabinetReady) {
        if (properties.product)
          dispatch(loadFurnitureAction(properties.product));
        else if (
          getContext(window.location.href) === FurnitureEditorContext.sales
        ) {
          dispatch(loadFurnitureAction());
        } else {
          setShowTypeSelector(true);
        }
      }
    }
  }, [isConfiguratorReady, isCabinetReady]);

  useEffect(() => {
    if (isConfiguratorReady) {
      if (properties.configuratorDidLoadCallback)
        properties.configuratorDidLoadCallback();
    }
  }, [isCabinetReady]);

  let schemaItem: Furniture | Cabinet | null = furniture;
  if (furniture && selectedCabinetId) {
    schemaItem = furniture.get(selectedCabinetId);
  }

  if (isConfiguratorReady) {
    return (
      <div>
        <GpuInfo />
        {/*<div className="bg-green-lighter h-24 w-full">Kewlox header</div>*/}
        {/*<div className="bg-blue-lighter h-56 w-full">Product infos</div>*/}
        {/*<div className="bg-blue-lighter h-56 w-full">Product infos2</div>*/}

        <div className="flex flex-col-reverse lg:flex-col kewconf-screen">
          <div className="header-configurator flex-shrink-0 flex-grow-0">
            {!isMobile && (
              <>
                <SupportPanel />
                <PanesPanel />
                <PlatePanel />
                <DrawersCompositionPanel />
                <DrawersPanel />
                <IntermediateShelfPanel />
                <HandlesPanel />
                <FurniturePanel />
                <CrossPiecePanel />
                <PaintPanel />
                <UpperShelfPanel />
              </>
            )}
            <Header />
            <Ribbon />
          </div>
          <StyledContentContainer className="content-configurator flex flex-col-reverse lg:flex-row flex-shrink flex-grow min-h-0 h-full">
            <div className="flex-shrink flex-grow min-h-0 flex lg:w-2/3">
              {!isLoadingViewer ? (
                <Styled3DViewerContainer className="viewer w-3/4 lg:w-full">
                  {isVisibleViewer && (
                    <div className="viewer-container h-full relative">
                      <Viewer />
                      {displayDimension && schemaItem && (
                        <SchemaDimensionsContainer item={schemaItem} />
                      )}
                      <IconsCanvas />
                    </div>
                  )}
                </Styled3DViewerContainer>
              ) : (
                <div className="flex justify-center items-center w-full">
                  <CircleSpinner size={40} color="#4a5568" />
                </div>
              )}
              <div className="w-1/4 lg:hidden flex flex-col justify-between items-center">
                <div>
                  <FurnitureInfos />
                  <AddToCart />
                  <DimensionInfos />
                  {/*{isMobile && <SeeSchemaDimensionBtn />}*/}
                </div>
                <div className="flex justify-center">
                  {isMobile && <IconBarCanvasMobile />}
                </div>
              </div>
            </div>
            <StyledInfosConfiguratorContainer className="flex-shrink flex-grow infos-configurator lg:w-1/3 overflow-auto">
              <div className="flex flex-col h-full">
                {isVisiblePieceList ? <PiecesList /> : <Info />}
              </div>
            </StyledInfosConfiguratorContainer>
            {isMobile && <FurniturePanel />}
          </StyledContentContainer>

          <ControlConfigurator className="control-configurator flex-shrink-0 flex-grow-0 h-12" />
        </div>
        {!isMobile && <TutorialTour />}
        {process.env.NODE_ENV !== 'production' && <PackagingInfos />}
        {process.env.NODE_ENV === 'production' &&
          context === FurnitureEditorContext.webShop && <Description />}
        {/*<div className="bg-blue-lightest h-56 w-full">Kewlox footer</div>*/}
        {/*<div className="text-2xl bg-blue">{BASE_URL}</div>*/}
      </div>
    );
  } else {
    return (
      <>
        {showTypeSelector ? (
          <TypeCabinetModalSelector setShowTypeSelector={setShowTypeSelector} />
        ) : (
          <div className="flex flex-col justify-center items-center h-48">
            <div className="flex items-center">
              <div>
                <div className="mb-2">
                  <ColorLinearProgress />
                </div>
                <div className="mr-3">{t('The configurator is loading')}</div>
              </div>
            </div>
          </div>
        )}

        {/*<div className="w-16 border border-gray-700 px-3 py-1 align-middle">
          <button
            onClick={() => {
              localStorage.removeItem('furniture');
              window.location.reload(true);
            }}
          >
            {t('Reset')}
          </button>
        </div>*/}
      </>
    );
  }
}

export default Configurator;

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  background: linear-gradient(0deg, #f0f0f0, rgb(250, 250, 250));

  @media (min-width: 1024px) {
    flex-direction: row;
  }
`;

const Styled3DViewerContainer = styled.div<{ height?: number }>`
  height: ${p => (p.height !== undefined ? `${p.height}px` : 'inherit')};
`;

const StyledInfosConfiguratorContainer = styled.div`
  min-height: 7rem;
`;
